import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import { HeaderNav } from "./components/HeaderNav";
import { Footer } from "./components/Footer";
import HomePage from "./pages/HomePage";
import BlogPage from "./pages/BlogPage";
import ArticlePage from "./pages/ArticlePage";
import BookingPage from "./pages/BookingPage";
import AboutPage from "./pages/AboutPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

const SmoothScroll = ({ children }) => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen grid place-items-center bg-alabaster">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#757A70]">
          A verificar sessão…
        </span>
      </div>
    );
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

const NotFound = () => {
  const { t } = useLanguage();
  return (
    <main className="pt-40 pb-32 min-h-screen text-center" data-testid="not-found-page">
      <p className="overline-label mb-6">404</p>
      <h1 className="font-serif italic text-3xl sm:text-4xl text-forest mb-8">{t("notFound.title")}</h1>
      <Link to="/" data-testid="not-found-home-link" className="btn-outline">{t("notFound.back")}</Link>
    </main>
  );
};

const SEO_MAP = {
  "/": ["seo.home", "seo.home.desc"],
  "/sobre": ["seo.about", "seo.about.desc"],
  "/blog": ["seo.blog", "seo.blog.desc"],
  "/agendamento": ["seo.booking", "seo.booking.desc"],
};

const SeoRoute = () => {
  const { pathname } = useLocation();
  const { t } = useLanguage();
  useEffect(() => {
    const keys = SEO_MAP[pathname];
    if (!keys) return;
    document.title = `${t(keys[0])} | Luísa Azevedo Lopes`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t(keys[1]));
  }, [pathname, t]);
  return null;
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      <ScrollToTop />
      <SeoRoute />
      <div className="grain-overlay" aria-hidden="true" />
      {!isAdmin && <HeaderNav />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/sobre" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<ArticlePage />} />
        <Route path="/agendamento" element={<BookingPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAdmin && <Footer />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <SmoothScroll>
            <Shell />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#213023",
                  color: "#F9F9F6",
                  border: "1px solid #3D523B",
                  borderRadius: "2px",
                  fontFamily: "Manrope, sans-serif",
                },
              }}
            />
          </SmoothScroll>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
